import { User } from '../@types';

export type IpaRouteAccess = 'NEGOTIATION' | 'CAMPAIGNS' | 'PRICE_OPTIMIZATION' | 'SDD' | 'PRO';

export const IPA_ADDITIONAL_CONTRACTS = {
  NEGOTIATION: 'MODULO_NEGOCIACAO',
  CAMPAIGNS: 'CAMPANHAS_PROMOCIONAIS',
  PRICE_OPTIMIZATION: 'OTIMIZACAO_DE_PRECOS',
} as const;

export const IPA_NEGOTIATION_HOME_PATH = '/ipa/negociacoes/fornecedor';
export const IPA_PRO_HOME_PATH = '/ipa/gerenciador-de-precos';
export const SDD_HOME_PATH = '/ipa/calculadora-demanda';

// O BASIC e uma excecao dentro do IPA: ele acessa somente as rotas marcadas
// como NEGOTIATION, mesmo sem adicional de contrato, mas continua bloqueado
// para a area PRO mais ampla. Os helpers abaixo deixam essa separacao explicita
// para o guard de rota nao tratar todo `/ipa/*` como a mesma permissao.
export const isIpaBasicPackage = (user?: User | null) => user?.servicoIpa?.pacoteIpa === 'BASIC';

export const hasIpaAdditionalContract = (user: User | null | undefined, contract: string) => {
  return (user?.tipoAdicionalContratos?.IPA ?? []).includes(contract);
};

export const hasService = (user: User | null | undefined, serviceName: string) => {
  return !!user?.servicos?.some(service => service.name === serviceName) || !!user?.servicosCliente?.some(service => service.name === serviceName);
};

// Negociacao tem regra propria:
// - BASIC acessa as rotas de negociacao porque esse e o modulo do pacote.
// - PRO so acessa negociacao quando MODULO_NEGOCIACAO esta nos adicionais.
// Por isso negociacao nao pode cair na regra generica de rotas PRO.
export const canAccessIpaNegotiation = (user?: User | null) => {
  return hasService(user, 'IPA') && (isIpaBasicPackage(user) || hasIpaAdditionalContract(user, IPA_ADDITIONAL_CONTRACTS.NEGOTIATION));
};

// Campanhas tambem tem regra propria:
// - BASIC nunca acessa campanhas.
// - PRO so acessa campanhas quando CAMPANHAS_PROMOCIONAIS esta nos adicionais.
// Esse helper evita abrir URLs de campanhas para todo usuario nao BASIC.
export const canAccessIpaCampaigns = (user?: User | null) => {
  return hasService(user, 'IPA') && !isIpaBasicPackage(user) && hasIpaAdditionalContract(user, IPA_ADDITIONAL_CONTRACTS.CAMPAIGNS);
};

// Otimizacao de precos segue o mesmo formato de campanhas:
// - BASIC nunca acessa.
// - PRO so acessa quando OTIMIZACAO_DE_PRECOS esta nos adicionais do contrato.
export const canAccessIpaPriceOptimization = (user?: User | null) => {
  return hasService(user, 'IPA') && !isIpaBasicPackage(user) && hasIpaAdditionalContract(user, IPA_ADDITIONAL_CONTRACTS.PRICE_OPTIMIZATION);
};

export const canAccessIpaRoute = (user: User | null | undefined, routeAccess?: IpaRouteAccess) => {
  if (!routeAccess) return true;
  if (!user) return false;
  if (routeAccess === 'NEGOTIATION') return canAccessIpaNegotiation(user);
  if (routeAccess === 'CAMPAIGNS') return canAccessIpaCampaigns(user);
  if (routeAccess === 'PRICE_OPTIMIZATION') return canAccessIpaPriceOptimization(user);
  if (routeAccess === 'SDD') return hasService(user, 'SDD');
  return hasService(user, 'IPA') && !isIpaBasicPackage(user);
};

// A Negociacao manual (o simulador tributario) mora fora de /ipa/, mas e liberada
// pela mesma regra de Negociacao fornecedor e Negociacoes salvas.
const IPA_NEGOTIATION_ROUTE_PATHS = ['/ipa/negociacoes/fornecedor/', '/ipa/negociacoes/salvas/', '/ipa/negociacoes/salvas/:id', '/simulador', '/simulador/:id'];
const SDD_ROUTE_PATHS = ['/ipa/calculadora-demanda', '/ipa/cenarios-salvos', '/ipa/comparacao-cenarios'];

// Diz qual regra de acesso a guarda de rota aplica a cada caminho configurado.
// Caminho sem regra devolve undefined: a rota fica so com a exigencia de login.
export const getIpaRouteAccessByPath = (path: string): IpaRouteAccess | undefined => {
  if (IPA_NEGOTIATION_ROUTE_PATHS.includes(path)) return 'NEGOTIATION';
  if (SDD_ROUTE_PATHS.includes(path)) return 'SDD';
  if (path.startsWith('/ipa/campanhas')) return 'CAMPAIGNS';
  if (path.startsWith('/ipa/ia/otimizacao-de-precos')) return 'PRICE_OPTIMIZATION';
  if (path.startsWith('/ipa/')) return 'PRO';
  return undefined;
};

// Fallbacks levam o usuario para a area IPA mais adequada ao perfil dele:
// - BASIC bloqueado em PRO ou CAMPAIGNS volta para negociacao, que e a home
//   dele dentro do IPA.
// - PRO bloqueado em NEGOTIATION ou CAMPAIGNS volta para a home PRO generica.
export const getIpaRouteFallbackPath = (user: User | null | undefined, routeAccess?: IpaRouteAccess) => {
  if (!hasService(user, 'IPA')) {
    if (hasService(user, 'SDD')) return SDD_HOME_PATH;
    if (hasService(user, 'PAN')) return user?.servicoPanel?.pacotePainel?.name === 'FREE' ? '/isa/painel-geral-free' : '/isa/painel-geral';
    if (hasService(user, 'COL')) return '/isa/historico-preco';
    if (hasService(user, 'IRA')) return '/ira';
    if (hasService(user, 'BI') && user?.servicoBi?.analises?.length) return `/analise/${user.servicoBi.analises[0]?.codigo}`;
    if (hasService(user, 'BI') && user?.servicoBi?.graficos?.length) return `/acompanhamento/${user.servicoBi.graficos[0]?.codigo}`;
    return '/login';
  }

  if ((routeAccess === 'NEGOTIATION' || routeAccess === 'CAMPAIGNS' || routeAccess === 'PRICE_OPTIMIZATION') && !isIpaBasicPackage(user)) {
    return IPA_PRO_HOME_PATH;
  }

  return IPA_NEGOTIATION_HOME_PATH;
};
